import api from "./api";
import { ok, isoUtc, nameOf, reasonOf, getMe, can } from "./core";

export const APPROVAL_STATUS = {
  PENDING: { label: "Awaiting approval", tone: "info" },
  APPROVED: { label: "Approved", tone: "success" },
  RETURNED: { label: "Returned", tone: "warning" },
  SUPERSEDED: { label: "Replaced by a correction", tone: "neutral" },
  NOT_REQUIRED: { label: "Recorded", tone: "neutral" },
};

const pct = (v) => (v === null || v === undefined ? null : Number(v));

const toApproval = (a) => ({
  id: a.progress_id,
  status: a.approval_status,
  statusLabel: APPROVAL_STATUS[a.approval_status]?.label || a.approval_status,
  tone: APPROVAL_STATUS[a.approval_status]?.tone || "neutral",
  recordId: a.performance_record_id,
  cycleId: a.cycle_id,
  employeeId: a.employee_id,
  employeeName: a.employee_name || nameOf(a.employee_id),
  itemId: a.performance_item_id,
  itemType: a.item_type,
  itemTitle: a.item_title,
  asOf: a.as_of_date,
  progress: pct(a.progress_pct),
  actual: pct(a.actual),
  completed: Boolean(a.mark_completed),
  comment: a.comment || "",
  effectiveProgress: pct(a.effective_progress_pct),
  submittedAt: isoUtc(a.submitted_at),
  decidedBy: a.decided_by_employee_id ? nameOf(a.decided_by_employee_id) : "",
  decidedAt: isoUtc(a.decided_at),
  decisionComment: a.decision_comment || "",
  isCorrection: Boolean(a.supersedes_progress_id),
  becomesCurrent: a.becomes_current === undefined ? true : Boolean(a.becomes_current),
});

export async function canApproveProgress() {
  return can(await getMe(), "progress.team.approve", ["TEAM", "SYSTEM_ALL"]);
}

export async function listProgressApprovals({ view = "all", status = "PENDING", recordId, itemId } = {}) {
  const { data } = await api.get("/progress-approvals", { params: { view, status, record_id: recordId, item_id: itemId, limit: 200 } });
  return ok(data.map(toApproval));
}

// [NEW] How many progress updates (goal / KRA / KPI) wait for this manager. Used by the sidebar badge, the
// manager dashboard and the notification pop-up. Never throws (people without the permission just get 0).
export async function countPendingProgress(cycleId) {
  try {
    const { data } = await listProgressApprovals({ view: "review", status: "PENDING" });
    const rows = cycleId ? data.filter((r) => String(r.cycleId) === String(cycleId)) : data;
    return { count: rows.length, rows };
  } catch {
    return { count: 0, rows: [] };
  }
}

export function decideProgress(progressId, decision, comment) {
  const text = String(comment || "").trim();
  if (decision === "RETURN" && !text) throw new Error("Tell the employee what to change (this comment is shown to them).");
  return api.post(`/progress-approvals/${progressId}/decision`, { decision, reason: reasonOf(text, "Progress approved") });
}

export const isPendingApproval = (progressResponse) => progressResponse?.approval_status === "PENDING";
